import { useCallback, useEffect, useRef, useState, type Dispatch, type SetStateAction } from "react";
import { fetchGlManagementTree } from "../../data/api/glManagementTreeApi";
import {
  mapDtoToGlTreeNode,
  type GlTreeNode,
} from "../../data/mappers/GlManagementTreeMapper";

const PAGE_SIZE = 20;

export interface UseGlManagementTreeReturn {
  items: GlTreeNode[];
  totalItems: number;
  hasMore: boolean;
  loading: boolean;
  error: string | null;
  page: number;
  setPage: Dispatch<SetStateAction<number>>;
  searchQuery: string;
  setSearchQuery: (value: string) => void;
  refetch: () => Promise<void>;
  pageSize: number;
}

export function useGlManagementTree(): UseGlManagementTreeReturn {
  const [searchQuery, setSearchQueryState] = useState("");
  const [page, setPage] = useState(0);
  const [items, setItems] = useState<GlTreeNode[]>([]);
  const [totalItems, setTotalItems] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const fetchRequestIdRef = useRef(0);
  const searchDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [debouncedSearch, setDebouncedSearch] = useState("");

  const setSearchQuery = useCallback((value: string) => {
    setSearchQueryState(value);
    if (searchDebounceRef.current) {
      clearTimeout(searchDebounceRef.current);
    }
    searchDebounceRef.current = setTimeout(() => {
      setDebouncedSearch(value.trim());
      setPage(0);
    }, 300);
  }, []);

  const loadTree = useCallback(async (pageIndex: number, search: string) => {
    const requestId = ++fetchRequestIdRef.current;
    setLoading(true);
    setError(null);
    try {
      const response = await fetchGlManagementTree({
        limit: PAGE_SIZE,
        offset: pageIndex * PAGE_SIZE,
        search: search || undefined,
      });
      if (requestId !== fetchRequestIdRef.current) {
        return;
      }
      const nodes = (response.data.items ?? []).map(mapDtoToGlTreeNode);
      setItems(nodes);
      setTotalItems(response.data.pagination?.total_items ?? 0);
      setHasMore(Boolean(response.data.pagination?.has_more));
    } catch (err) {
      if (requestId !== fetchRequestIdRef.current) {
        return;
      }
      const message =
        err instanceof Error ? err.message : "Failed to load GL mappings";
      setError(message);
      setItems([]);
      setTotalItems(0);
      setHasMore(false);
    } finally {
      if (requestId === fetchRequestIdRef.current) {
        setLoading(false);
      }
    }
  }, []);

  useEffect(() => {
    void loadTree(page, debouncedSearch);
  }, [loadTree, page, debouncedSearch]);

  useEffect(() => {
    return () => {
      if (searchDebounceRef.current) {
        clearTimeout(searchDebounceRef.current);
      }
    };
  }, []);

  const refetch = useCallback(async () => {
    await loadTree(page, debouncedSearch);
  }, [loadTree, page, debouncedSearch]);

  return {
    items,
    totalItems,
    hasMore,
    loading,
    error,
    page,
    setPage,
    searchQuery,
    setSearchQuery,
    refetch,
    pageSize: PAGE_SIZE,
  };
}
